/**
 * Blocking browser dialogs (the app is web-only, D1). `Alert.alert` from
 * react-native is a no-op on web, so these wrap `window.alert` /
 * `window.confirm` and own the title/message formatting: call sites pass
 * `(title, message)` and never format for web themselves.
 *
 * For non-blocking notices (success, "copied", ...) prefer `useToast()`.
 */

export type DialogButton = {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
};

/** The single text block a browser dialog shows for a title + optional message. */
export function formatDialogText(title: string, message?: string): string {
  return message ? `${title}\n\n${message}` : title;
}

function browserWindow(): (Window & typeof globalThis) | undefined {
  return typeof window !== 'undefined' ? window : undefined;
}

/**
 * Show a message. Buttons only matter for their callbacks, since a browser
 * alert cannot render them:
 * - a single button: the alert's OK is that button, so its `onPress` runs
 *   after dismissal;
 * - one action plus a cancel button: `window.confirm`, then the action's or
 *   the cancel button's `onPress` runs, matching what the user chose;
 * - several actions: a browser alert cannot ask which, so none runs.
 * An action never runs unless the user chose it (FE-6).
 */
export function showDialog(title: string, message?: string, buttons?: DialogButton[]): void {
  const win = browserWindow();
  if (!win) return;
  const text = formatDialogText(title, message);
  const actions = (buttons ?? []).filter((button) => button.style !== 'cancel');
  const cancel = (buttons ?? []).find((button) => button.style === 'cancel');

  if (actions.length === 1 && cancel) {
    if (win.confirm(text)) actions[0].onPress?.();
    else cancel.onPress?.();
    return;
  }

  win.alert(text);
  if (actions.length === 1) actions[0].onPress?.();
  else if (actions.length === 0) cancel?.onPress?.();
}

/** Resolve true when the user confirms; false on cancel or without a browser window. */
export function confirmDialog(
  title: string,
  message: string,
  // Browser confirm dialogs use the browser's own OK/Cancel labels; the labels
  // stay in the signature so call sites keep naming the actions.
  _confirmLabel: string,
  _cancelLabel: string,
): Promise<boolean> {
  const win = browserWindow();
  if (!win) return Promise.resolve(false);
  return Promise.resolve(win.confirm(formatDialogText(title, message)));
}
